import React, {useCallback, useRef} from "react";
import {InputLayout} from "./InputLayout";
import {isNumber} from "../../Utilities/Types/typeUtilities";
import {helpTextSuffix, notificationSuffix} from "./inputConstants";

export interface InputAdapterProps {
    autoComplete?: string;
    disabled?: boolean;
    error?: string;
    helpText?: string;
    hideLabel?: boolean;
    inputType?: string;
    label: string;
    labelClass?: string;
    large_columns?: string;
    max?: number;
    medium_columns?: string;
    min?: number;
    name: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    placeholder?: string;
    raiseHelpText?: boolean;
    responseFeedback?: string;
    small_columns?: string;
    step?: string;
    value?: string | number;
    [key: string]: unknown;
}

export const InputAdapter = React.memo<InputAdapterProps>(({
                                                               autoComplete,
                                                               disabled,
                                                               error,
                                                               helpText,
                                                               hideLabel,
                                                               inputType,
                                                               label: labelProp,
                                                               labelClass,
                                                               large_columns,
                                                               max,
                                                               medium_columns,
                                                               min,
                                                               name,
                                                               onChange,
                                                               placeholder,
                                                               raiseHelpText,
                                                               responseFeedback,
                                                               small_columns,
                                                               step,
                                                               value = "",
                                                           }) => {
    const changeHandlerEnabled = useRef(true);

    const handleOnChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        if (changeHandlerEnabled.current) {
            onChange?.(event);
        }
    }, [onChange]);

    const handleOnInput = useCallback((event: React.FormEvent<HTMLInputElement>) => {
        changeHandlerEnabled.current = false;
        onChange?.(event as unknown as React.ChangeEvent<HTMLInputElement>);
    }, [onChange]);

    let label = labelProp;
    if (isNumber(min) && !isNumber(max))
        label = `${label} (minimum: ${min})`;
    if (isNumber(max) && !isNumber(min))
        label = `${label} (maximum: ${max})`;
    if (isNumber(min) && isNumber(max))
        label = `${label} (range: ${min} - ${max})`;

    return (
        <InputLayout
            disabled={disabled}
            error={error}
            helpText={helpText}
            hideLabel={hideLabel}
            raiseHelpText={raiseHelpText}
            label={label}
            labelClass={labelClass}
            large_columns={large_columns}
            medium_columns={medium_columns}
            name={name}
            responseFeedback={responseFeedback}
            small_columns={small_columns}
        >
            <input
                aria-disabled={disabled}
                aria-invalid={!!error}
                aria-errormessage={error ? `${name}${notificationSuffix}` : undefined}
                aria-describedby={helpText ? `${name}${helpTextSuffix}` : undefined}
                autoComplete={autoComplete ? autoComplete : "off"}
                data-testid={name}
                disabled={disabled}
                id={name}
                min={min}
                max={max}
                name={name}
                onChange={handleOnChange}
                onInput={handleOnInput}
                placeholder={placeholder}
                step={step}
                type={inputType}
                value={value ?? ""}
            />
        </InputLayout>
    );
});
